<template>
  <div id="footer" class="flex-none hidden lg:block">
    <!-- Start Bottom Desktop -->
    <div class="w-auto h-auto !bg-opacity-90 !backdrop-blur-lg">
      <div class="relative flex justify-between items-center">
        <!-- Start Bottom Left -->
        <div class="flex items-center !pl-10 pb-4 gap-2">
          <!-- Reduce max-width by the width of the UI elements bottom right to avoid collision -->
          <div class="bg-white z-10">
            <UploadButton @click="uploadFile" />
          </div>

          <div class="overflow-x-scroll overflow-auto" style="max-width: calc(100vw - 350px)">
            <div class="flex gap-2 items-center">
              <div v-for="image in store.images" :key="image.meta.id" @click="store.selectImage(image)">
                <ImageButton
                  v-if="
                    image.previewResult.state === ProcessingState.Error || image.original.state === UploadState.Error
                  "
                  :selected="store.selectedImage === image"
                  class="relative"
                >
                  <div class="bg-typo text-white flex justify-center items-center bg-opacity-80 absolute w-full h-full">
                    <WarningIcon />
                  </div>
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>

                <ImageButton
                  v-else-if="image.previewResult.state === ProcessingState.Processing"
                  :selected="store.selectedImage === image"
                  class="relative"
                  loading
                >
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>

                <ImageButton v-else :selected="store.selectedImage === image">
                  <HTMLImage
                    :src="previewUrl(image)"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>
              </div>

              <!-- Empty element to have some more space for horizontal scrolling -->
              <div class="block shrink-0 !w-12 !h-12 sm:!w-16 sm:!h-16 pointer-events-none"></div>
            </div>
          </div>
        </div>
        <!-- End Bottom Left -->
        <!-- Start Bottom Right -->
        <div class="flex gap-2 items-center px-6 py-4 rounded-2xl">
          <!-- Start Rating -->
          <div class="flex items-center mr-2">
            <Rating :image="store.selectedImage" />
          </div>
          <!-- End Rating -->

          <!-- Start Divider -->
          <span class="h-4 w-[1px] rounded-full bg-secondary-hover"></span>
          <!-- End Divider -->

          <!-- Trash Icon / Delete Image -->
          <IconButton
            v-if="!requiresDeleteConfirmation"
            variant="secondary"
            :title="I18n.t('editor.delete_selected')"
            @click.prevent="() => deleteImage(store.selectedImage)"
          >
            <TrashIcon />
          </IconButton>
          <Dialog v-else trigger-classes="focus:outline-none">
            <template #trigger>
              <IconButton variant="secondary">
                <TrashIcon />
              </IconButton>
            </template>
            <template v-slot:content="{ closeDialog }">
              <DeleteImageDialog
                :on-delete="() => deleteImage(store.selectedImage, closeDialog)"
                :on-cancel="closeDialog"
              />
            </template>
          </Dialog>
        </div>
        <!-- End Bottom Right -->
      </div>
    </div>
    <!-- End Bottom Desktop -->
  </div>
</template>

<script setup lang="ts">
import { Image as HTMLImage, ImageButton, WarningIcon, TrashIcon, IconButton, UploadButton, Dialog } from "prism";
import { Image, UploadState, ProcessingState } from "@/modules/internal_api/image";
import { rbgRemoveImageV100 } from "kaleido-event-tracker";
import Rating from "@/components/prism/rating.vue";
import originalPlaceholder from "@assets/images/inline-editor/original_placeholder.jpg";
import DeleteImageDialog from "./editor_components/delete_image_dialog.vue";

import Client from "@/modules/internal_api/client";

import { useEditorStore } from "@/stores/editor_store";
import { useDeleteConfirmation } from "@/composables/delete_confirmation";

const store = useEditorStore();
const { requiresDeleteConfirmation } = useDeleteConfirmation();

const uploadFile = () => {
  window.onUploadFile();
};

const previewUrl = (image: Image) => {
  if (
    image.meta.preview === originalPlaceholder &&
    image.original.state === UploadState.Finished &&
    "url" in image.original
  ) {
    return image.original.url;
  }
  return image.meta.preview;
};

const deleteImage = (image: Image, closeDialog?: () => void) => {
  Client.deleteImage(image);
  store.deleteImage(image);
  if (closeDialog) closeDialog();
  window.track("Images", "remove_result", "Remove result");
  rbgRemoveImageV100({ image_id: image.meta.id });
};
</script>
